import { useState, useEffect } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { supabase } from '../../../lib/supabase';
import { mockTeamMembers, mockSettings, TeamMember } from '../../../mocks/step6Data';

type TaskCategory = 'gunluk' | 'haftalik' | 'aylik';

interface Task {
  id: string;
  title: string;
  description: string;
  icon: string;
  color: string;
  bg: string;
  completed: boolean;
  category: TaskCategory;
  points: number;
  custom: boolean;
}

interface SupabaseTask {
  id: string;
  user_id: string;
  title: string;
  description: string;
  task_type: string;
  completed: boolean;
  points: number;
  is_custom: boolean;
}

interface SupabaseMember {
  id: string;
  user_id: string;
  name: string;
  phone: string;
  status: string;
  step1_completed: boolean;
  last_contact: string;
  note: string;
  join_date: string;
  parent_id: string | null;
}

const defaultTaskVisuals: Record<string, { icon: string; color: string; bg: string }> = {
  'Sosyal medyada paylaşım yap': { icon: 'ri-instagram-line', color: 'text-pink-400', bg: 'rgba(236,72,153,0.1)' },
  '3 potansiyel kişiyle iletişime geç': { icon: 'ri-chat-3-line', color: 'text-green-400', bg: 'rgba(34,197,94,0.1)' },
  'Lider videosunu izle': { icon: 'ri-play-circle-line', color: 'text-amber-400', bg: 'rgba(245,166,35,0.1)' },
  'Ekip mesajlarını kontrol et': { icon: 'ri-whatsapp-line', color: 'text-teal-400', bg: 'rgba(20,184,166,0.1)' },
  'Aylık ürün alımını gerçekleştir': { icon: 'ri-shopping-bag-3-line', color: 'text-blue-400', bg: 'rgba(59,130,246,0.1)' },
  'Yeni ekip üyesi davet et': { icon: 'ri-user-add-line', color: 'text-violet-400', bg: 'rgba(139,92,246,0.1)' },
  'Ekip üyelerine sistem eğitimi ver': { icon: 'ri-presentation-line', color: 'text-orange-400', bg: 'rgba(249,115,22,0.1)' },
  'Kazanç simülasyonunu güncelle': { icon: 'ri-line-chart-line', color: 'text-cyan-400', bg: 'rgba(6,182,212,0.1)' },
  '4 kişinin 1. adımı tamamlamasını sağla': { icon: 'ri-checkbox-circle-line', color: 'text-amber-400', bg: 'rgba(245,166,35,0.1)' },
  'Hedef gelire ulaş': { icon: 'ri-money-cny-circle-line', color: 'text-green-400', bg: 'rgba(34,197,94,0.1)' },
  'Bir sonraki adıma hazırlan': { icon: 'ri-arrow-up-circle-line', color: 'text-rose-400', bg: 'rgba(244,63,94,0.1)' },
};

const customTaskVisuals = { icon: 'ri-star-line', color: 'text-amber-400', bg: 'rgba(245,166,35,0.1)' };

function buildDefaultTasks(aktiflikBedeli: number, teamCount: number, step1Completed: number): Omit<SupabaseTask, 'id' | 'user_id'>[] {
  return [
    { title: 'Sosyal medyada paylaşım yap', description: 'Instagram veya Facebook\'ta STEP6 ile ilgili bir içerik paylaş', task_type: 'gunluk', completed: false, points: 10, is_custom: false },
    { title: '3 potansiyel kişiyle iletişime geç', description: 'WhatsApp veya yüz yüze en az 3 kişiyle sistemi anlat', task_type: 'gunluk', completed: false, points: 15, is_custom: false },
    { title: 'Lider videosunu izle', description: 'Günlük motivasyon için bir lider videosu seyret', task_type: 'gunluk', completed: false, points: 5, is_custom: false },
    { title: 'Ekip mesajlarını kontrol et', description: 'WhatsApp grubunu ve mesajlarını yanıtla', task_type: 'gunluk', completed: false, points: 5, is_custom: false },
    { title: 'Aylık ürün alımını gerçekleştir', description: `Aktiflik için ₺${aktiflikBedeli.toLocaleString('tr-TR')} tutarında ürün satın al`, task_type: 'haftalik', completed: false, points: 30, is_custom: false },
    { title: 'Yeni ekip üyesi davet et', description: '4 kişilik kotanı doldurmak için en az 1 kişiyi ekle', task_type: 'haftalik', completed: teamCount >= 1, points: 25, is_custom: false },
    { title: 'Ekip üyelerine sistem eğitimi ver', description: 'Yeni ekip üyelerine STEP6 sistemini anlatan bir sohbet yap', task_type: 'haftalik', completed: false, points: 20, is_custom: false },
    { title: 'Kazanç simülasyonunu güncelle', description: 'STEP6 Planım sayfasında gelir projeksiyonunu kontrol et', task_type: 'haftalik', completed: false, points: 10, is_custom: false },
    { title: '4 kişinin 1. adımı tamamlamasını sağla', description: '4 kişinin tamamının aktif olmasını ve 1. adımı bitirmesini sağla', task_type: 'aylik', completed: step1Completed >= 4, points: 50, is_custom: false },
    { title: 'Hedef gelire ulaş', description: `Bu ay ₺${(aktiflikBedeli * 4).toLocaleString('tr-TR')} net kazanç elde et`, task_type: 'aylik', completed: false, points: 60, is_custom: false },
    { title: 'Bir sonraki adıma hazırlan', description: '2. adıma geçiş kriterlerini öğren ve ekibini büyüt', task_type: 'aylik', completed: false, points: 40, is_custom: false },
  ];
}

function mapSupabaseTask(st: SupabaseTask): Task {
  const visuals = defaultTaskVisuals[st.title] || customTaskVisuals;
  return {
    id: st.id,
    title: st.title,
    description: st.description,
    icon: visuals.icon,
    color: visuals.color,
    bg: visuals.bg,
    completed: st.completed,
    category: st.task_type as TaskCategory,
    points: st.points,
    custom: st.is_custom,
  };
}

const categoryMeta: Record<TaskCategory, { label: string; icon: string; color: string; accent: string }> = {
  gunluk:   { label: 'Günlük Görevler',   icon: 'ri-sun-line',        color: 'text-amber-400',  accent: '#F5A623' },
  haftalik: { label: 'Haftalık Görevler', icon: 'ri-calendar-line',   color: 'text-blue-400',   accent: '#3B82F6' },
  aylik:    { label: 'Aylık Hedefler',    icon: 'ri-trophy-line',     color: 'text-green-400',  accent: '#22C55E' },
};

interface BenimAdimimProps {
  onNavigate: (section: string) => void;
}

export default function BenimAdimim({ onNavigate }: BenimAdimimProps) {
  const { profile } = useAuth();
  const [status, setStatus] = useState<'aktif' | 'pasif'>('aktif');
  const [tasks, setTasks] = useState<Task[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [aktiflikBedeli, setAktiflikBedeli] = useState(1200);
  const [activeTab, setActiveTab] = useState<TaskCategory>('gunluk');
  const [addingTask, setAddingTask] = useState(false);
  const [newTaskLabel, setNewTaskLabel] = useState('');
  const [newTaskDesc, setNewTaskDesc] = useState('');
  const [expandedTask, setExpandedTask] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Load team members, settings, and tasks
  useEffect(() => {
    let cancelled = false;

    async function loadAll() {
      setLoading(true);

      if (!profile) {
        setTeamMembers(mockTeamMembers);
        setAktiflikBedeli(mockSettings.aktiflikBedeli);
        // Fallback to mock-like tasks
        const fallback = buildDefaultTasks(mockSettings.aktiflikBedeli, mockTeamMembers.length, mockTeamMembers.filter(m => m.step1Completed).length)
          .map((t, i) => ({ ...t, id: `mock-${i}`, user_id: '' }));
        setTasks(fallback.map(st => mapSupabaseTask(st as SupabaseTask)));
        setLoading(false);
        return;
      }

      try {
        // Load team members
        const { data: membersData } = await supabase
          .from('step6_team_members')
          .select('*')
          .eq('user_id', profile.id);

        let loadedMembers: TeamMember[] = mockTeamMembers;
        if (membersData && membersData.length > 0) {
          const allMembers = membersData as SupabaseMember[];
          const l1Members = allMembers.filter(m => !m.parent_id);
          const l2Members = allMembers.filter(m => m.parent_id);
          loadedMembers = l1Members.map(l1 => ({
            id: l1.id,
            name: l1.name,
            phone: l1.phone,
            status: l1.status as TeamMember['status'],
            step1Completed: l1.step1_completed,
            lastContact: l1.last_contact,
            note: l1.note,
            joinDate: l1.join_date,
            ownTeam: l2Members.filter(l2 => l2.parent_id === l1.id).map(c => ({
              id: c.id,
              name: c.name,
              phone: c.phone,
              status: c.status as TeamMember['status'],
              step1Completed: c.step1_completed,
              lastContact: c.last_contact,
              note: c.note,
              joinDate: c.join_date,
            })),
          }));
        }

        // Load settings
        const { data: settingsData } = await supabase
          .from('step6_settings')
          .select('*')
          .eq('user_id', profile.id)
          .maybeSingle();

        let loadedBedel = mockSettings.aktiflikBedeli;
        if (settingsData) {
          loadedBedel = settingsData.aktiflik_bedeli || settingsData.aktiflikBedeli || mockSettings.aktiflikBedeli;
        }

        if (cancelled) return;
        setTeamMembers(loadedMembers);
        setAktiflikBedeli(loadedBedel);

        // Load tasks
        const { data: tasksData } = await supabase
          .from('step6_tasks')
          .select('*')
          .eq('user_id', profile.id)
          .order('created_at', { ascending: true });

        if (tasksData && tasksData.length > 0) {
          setTasks((tasksData as SupabaseTask[]).map(mapSupabaseTask));
        } else {
          // Seed default tasks
          const step1Done = loadedMembers.filter(m => m.step1Completed).length;
          const defaults = buildDefaultTasks(loadedBedel, loadedMembers.length, step1Done);
          const { data: seeded } = await supabase
            .from('step6_tasks')
            .insert(defaults.map(d => ({ ...d, user_id: profile.id })))
            .select();

          if (!cancelled && seeded) {
            setTasks((seeded as SupabaseTask[]).map(mapSupabaseTask));
          }
        }
      } catch (e) {
        if (!cancelled) {
          setError('Veri yüklenirken hata oluştu');
          setTeamMembers(mockTeamMembers);
          setAktiflikBedeli(mockSettings.aktiflikBedeli);
        }
      }

      if (!cancelled) setLoading(false);
    }

    loadAll();
    return () => { cancelled = true; };
  }, [profile]);

  const toggleTask = async (id: string) => {
    const task = tasks.find(t => t.id === id);
    if (!task) return;

    // Optimistic update
    setTasks(prev => prev.map(t => t.id === id ? { ...t, completed: !t.completed } : t));

    if (!profile) return;

    await supabase
      .from('step6_tasks')
      .update({ completed: !task.completed })
      .eq('id', id)
      .eq('user_id', profile.id);
  };

  const addCustomTask = async () => {
    if (!newTaskLabel.trim()) return;

    const newTitle = newTaskLabel.trim();
    const newDesc = newTaskDesc.trim() || 'Özel görev';

    if (!profile) {
      // Local-only fallback
      const localTask: Task = {
        id: `local-${Date.now()}`,
        title: newTitle,
        description: newDesc,
        icon: customTaskVisuals.icon,
        color: customTaskVisuals.color,
        bg: customTaskVisuals.bg,
        completed: false,
        category: activeTab,
        points: 10,
        custom: true,
      };
      setTasks(prev => [...prev, localTask]);
      setNewTaskLabel('');
      setNewTaskDesc('');
      setAddingTask(false);
      return;
    }

    const { data, error: insertError } = await supabase
      .from('step6_tasks')
      .insert({
        user_id: profile.id,
        title: newTitle,
        description: newDesc,
        task_type: activeTab,
        completed: false,
        points: 10,
        is_custom: true,
      })
      .select()
      .single();

    if (insertError || !data) return;

    setTasks(prev => [...prev, mapSupabaseTask(data as SupabaseTask)]);
    setNewTaskLabel('');
    setNewTaskDesc('');
    setAddingTask(false);
  };

  const deleteTask = async (id: string) => {
    setTasks(prev => prev.filter(t => t.id !== id));

    if (!profile) return;

    await supabase
      .from('step6_tasks')
      .delete()
      .eq('id', id)
      .eq('user_id', profile.id);
  };

  const step1Count = teamMembers.filter(m => m.step1Completed).length;
  const activeCount = teamMembers.filter(m => m.status === 'aktif' || m.status === 'tamamladi').length;

  const filtered = tasks.filter(t => t.category === activeTab);
  const doneCount = filtered.filter(t => t.completed).length;
  const totalCount = filtered.length;
  const pct = totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0;

  const totalPoints = tasks.filter(t => t.completed).reduce((sum, t) => sum + t.points, 0);
  const maxPoints = tasks.reduce((sum, t) => sum + t.points, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 rounded-full border-2 border-amber-400 border-t-transparent animate-spin" />
          <p className="text-sm text-gray-400">Görevler yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Benim Adımım</h1>
        <p className="text-sm text-gray-500 mt-1">STEP6 sistemindeki mevcut konumun ve görev takibin</p>
      </div>

      {error && (
        <div className="rounded-xl px-4 py-3 text-xs" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {/* Step Badge + Status Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-2xl p-5 flex flex-col items-center justify-center text-center" style={{ background: 'linear-gradient(135deg, rgba(245,166,35,0.12) 0%, rgba(255,107,53,0.08) 100%)', border: '1px solid rgba(245,166,35,0.2)' }}>
          <div className="text-5xl font-black text-amber-400 mb-2">1</div>
          <div className="text-sm font-semibold text-white">Mevcut Adım</div>
          <div className="text-xs text-gray-500 mt-1">STEP6 Yolculuğu</div>
        </div>

        <div className="rounded-2xl p-5" style={{ background: '#12141E', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="text-xs text-gray-500 mb-2 font-medium">Aktiflik Durumu</div>
          <div className="flex items-center gap-2 mb-3">
            <span className={`w-2.5 h-2.5 rounded-full ${status === 'aktif' ? 'bg-green-500' : 'bg-red-500'}`} />
            <span className={`text-lg font-bold ${status === 'aktif' ? 'text-green-400' : 'text-red-400'}`}>{status === 'aktif' ? 'Aktif' : 'Pasif'}</span>
          </div>
          <div className="text-xs text-gray-500 mb-3">Bu ay aktifliğin: <span className="text-white font-medium">₺{aktiflikBedeli.toLocaleString('tr-TR')}</span></div>
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
            <div className="h-full rounded-full bg-green-500 transition-all" style={{ width: status === 'aktif' ? '100%' : '30%' }} />
          </div>
          <p className="text-xs text-gray-600 mt-2">8 gün içinde yenileniyor</p>
        </div>

        <div className="rounded-2xl p-5" style={{ background: '#12141E', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="text-xs text-gray-500 mb-2 font-medium">İlk 4 Kişi & 1. Adım</div>
          <div className="flex gap-4 mb-3">
            <div>
              <div className="text-2xl font-bold text-blue-400">{teamMembers.length}/4</div>
              <div className="text-xs text-gray-500">Kişi</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-amber-400">{step1Count}/4</div>
              <div className="text-xs text-gray-500">1. Adım</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-400">{activeCount}/4</div>
              <div className="text-xs text-gray-500">Aktif</div>
            </div>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
            <div className="h-full rounded-full transition-all" style={{ width: `${(step1Count / 4) * 100}%`, background: 'linear-gradient(90deg, #F5A623, #FF6B35)' }} />
          </div>
          <p className="text-xs text-gray-600 mt-2">%{Math.round((step1Count / 4) * 100)} tamamlandı</p>
        </div>
      </div>

      {/* Puan Özeti */}
      <div className="rounded-2xl p-5 flex items-center gap-5" style={{ background: 'linear-gradient(135deg, rgba(245,166,35,0.1) 0%, rgba(255,107,53,0.06) 100%)', border: '1px solid rgba(245,166,35,0.18)' }}>
        <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(245,166,35,0.15)' }}>
          <i className="ri-medal-line text-amber-400 text-2xl" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-sm font-semibold text-white">Görev Puanları</span>
            <span className="text-sm font-bold text-amber-400">{totalPoints} / {maxPoints} puan</span>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${maxPoints > 0 ? Math.round((totalPoints / maxPoints) * 100) : 0}%`, background: 'linear-gradient(90deg, #F5A623, #FF6B35)' }} />
          </div>
          <p className="text-xs text-gray-500 mt-1.5">Toplam {tasks.filter(t => t.completed).length}/{tasks.length} görev tamamlandı · %{maxPoints > 0 ? Math.round((totalPoints / maxPoints) * 100) : 0} başarı</p>
        </div>
      </div>

      {/* Görev Takibi */}
      <div className="rounded-2xl overflow-hidden" style={{ background: '#12141E', border: '1px solid rgba(255,255,255,0.06)' }}>
        {/* Tab Header */}
        <div className="flex items-center gap-1 p-2" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', background: 'rgba(0,0,0,0.2)' }}>
          {(Object.keys(categoryMeta) as TaskCategory[]).map(cat => {
            const meta = categoryMeta[cat];
            const catTasks = tasks.filter(t => t.category === cat);
            const catDone = catTasks.filter(t => t.completed).length;
            const isActive = activeTab === cat;
            return (
              <button
                key={cat}
                onClick={() => setActiveTab(cat)}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${isActive ? 'text-white' : 'text-gray-500 hover:text-gray-300'}`}
                style={isActive ? { background: `rgba(${meta.accent === '#F5A623' ? '245,166,35' : meta.accent === '#3B82F6' ? '59,130,246' : '34,197,94'},0.15)`, border: `1px solid rgba(${meta.accent === '#F5A623' ? '245,166,35' : meta.accent === '#3B82F6' ? '59,130,246' : '34,197,94'},0.25)` } : { border: '1px solid transparent' }}>
                <div className={`w-4 h-4 flex items-center justify-center ${isActive ? meta.color : ''}`}>
                  <i className={`${meta.icon} text-sm`} />
                </div>
                <span>{meta.label}</span>
                <span className="px-1.5 py-0.5 rounded-full text-xs" style={{ background: 'rgba(255,255,255,0.08)' }}>{catDone}/{catTasks.length}</span>
              </button>
            );
          })}
        </div>

        {/* Category Progress */}
        <div className="px-5 pt-4 pb-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`w-6 h-6 flex items-center justify-center ${categoryMeta[activeTab].color}`}>
              <i className={`${categoryMeta[activeTab].icon} text-base`} />
            </div>
            <span className="text-sm font-semibold text-white">{categoryMeta[activeTab].label}</span>
          </div>
          <span className="text-xs font-bold" style={{ color: categoryMeta[activeTab].accent }}>{pct}% tamamlandı</span>
        </div>
        <div className="px-5 pb-4">
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${categoryMeta[activeTab].accent}, ${categoryMeta[activeTab].accent}99)` }} />
          </div>
          <p className="text-xs text-gray-600 mt-1.5">{doneCount} / {totalCount} görev tamamlandı</p>
        </div>

        {/* Task List */}
        <div className="px-5 pb-4 space-y-2">
          {filtered.length === 0 && (
            <div className="py-8 text-center">
              <p className="text-sm text-gray-500">Bu kategoride henüz görev yok.</p>
              <p className="text-xs text-gray-600 mt-1">"Özel Görev Ekle" ile yeni bir görev oluşturabilirsin.</p>
            </div>
          )}

          {filtered.map(task => (
            <div key={task.id}>
              <div
                className={`flex items-center gap-3 p-3.5 rounded-xl transition-all cursor-pointer ${task.completed ? '' : 'hover:bg-white/[0.02]'}`}
                style={{ background: task.completed ? 'rgba(34,197,94,0.04)' : 'rgba(255,255,255,0.025)', border: task.completed ? '1px solid rgba(34,197,94,0.12)' : '1px solid rgba(255,255,255,0.04)' }}
                onClick={() => setExpandedTask(expandedTask === task.id ? null : task.id)}
              >
                {/* Checkbox */}
                <button
                  onClick={e => { e.stopPropagation(); toggleTask(task.id); }}
                  className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-all cursor-pointer ${task.completed ? 'bg-green-500' : 'hover:bg-white/10'}`}
                  style={!task.completed ? { border: '2px solid rgba(255,255,255,0.15)' } : {}}
                >
                  {task.completed && <i className="ri-check-line text-white text-xs" />}
                </button>
                {/* Icon */}
                <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: task.bg }}>
                  <i className={`${task.icon} text-sm ${task.color}`} />
                </div>
                {/* Label */}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium leading-snug ${task.completed ? 'text-gray-500 line-through' : 'text-white'}`}>{task.title}</p>
                  {task.custom && <span className="text-xs text-amber-500 font-medium">Özel Görev</span>}
                </div>
                {/* Points + Expand */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-xs font-bold px-2 py-1 rounded-lg" style={{ background: 'rgba(245,166,35,0.1)', color: '#F5A623' }}>+{task.points}</span>
                  <div className={`w-5 h-5 flex items-center justify-center text-gray-600 transition-transform ${expandedTask === task.id ? 'rotate-180' : ''}`}>
                    <i className="ri-arrow-down-s-line text-base" />
                  </div>
                </div>
              </div>

              {/* Expanded Detail */}
              {expandedTask === task.id && (
                <div className="mx-3 px-4 py-3 rounded-b-xl" style={{ background: 'rgba(255,255,255,0.02)', borderLeft: `2px solid ${categoryMeta[activeTab].accent}40` }}>
                  <p className="text-xs text-gray-400 leading-relaxed">{task.description}</p>
                  <div className="flex items-center gap-3 mt-3">
                    <button
                      onClick={() => toggleTask(task.id)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-all"
                      style={{ background: task.completed ? 'rgba(239,68,68,0.12)' : 'rgba(34,197,94,0.12)', color: task.completed ? '#EF4444' : '#22C55E', border: `1px solid ${task.completed ? 'rgba(239,68,68,0.2)' : 'rgba(34,197,94,0.2)'}` }}
                    >
                      <i className={`${task.completed ? 'ri-close-circle-line' : 'ri-checkbox-circle-line'} text-sm`} />
                      {task.completed ? 'Tamamlanmadı Olarak İşaretle' : 'Tamamlandı Olarak İşaretle'}
                    </button>
                    {task.custom && (
                      <button
                        onClick={() => { deleteTask(task.id); setExpandedTask(null); }}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-all"
                        style={{ background: 'rgba(239,68,68,0.08)', color: '#EF4444', border: '1px solid rgba(239,68,68,0.15)' }}
                      >
                        <i className="ri-delete-bin-line text-sm" />
                        Sil
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Add Task */}
          {addingTask ? (
            <div className="p-4 rounded-xl space-y-3" style={{ background: 'rgba(245,166,35,0.04)', border: '1px solid rgba(245,166,35,0.15)' }}>
              <p className="text-xs font-semibold text-amber-400">Yeni Özel Görev</p>
              <input
                type="text"
                placeholder="Görev başlığı..."
                value={newTaskLabel}
                onChange={e => setNewTaskLabel(e.target.value)}
                className="w-full bg-transparent text-sm text-white placeholder-gray-600 outline-none border-b pb-1.5"
                style={{ borderColor: 'rgba(255,255,255,0.1)' }}
                onKeyDown={e => e.key === 'Enter' && addCustomTask()}
                autoFocus
              />
              <input
                type="text"
                placeholder="Açıklama (isteğe bağlı)..."
                value={newTaskDesc}
                onChange={e => setNewTaskDesc(e.target.value)}
                className="w-full bg-transparent text-xs text-gray-400 placeholder-gray-600 outline-none border-b pb-1.5"
                style={{ borderColor: 'rgba(255,255,255,0.06)' }}
                onKeyDown={e => e.key === 'Enter' && addCustomTask()}
              />
              <div className="flex gap-2">
                <button onClick={addCustomTask} disabled={!newTaskLabel.trim()}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold cursor-pointer disabled:opacity-40 whitespace-nowrap"
                  style={{ background: 'rgba(245,166,35,0.15)', color: '#F5A623', border: '1px solid rgba(245,166,35,0.25)' }}>
                  <i className="ri-check-line text-sm" />
                  Ekle
                </button>
                <button onClick={() => { setAddingTask(false); setNewTaskLabel(''); setNewTaskDesc(''); }}
                  className="px-4 py-2 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap"
                  style={{ background: 'rgba(255,255,255,0.04)', color: '#9CA3AF', border: '1px solid rgba(255,255,255,0.06)' }}>
                  Vazgeç
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setAddingTask(true)}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm text-gray-500 hover:text-gray-300 transition-all cursor-pointer"
              style={{ border: '1px dashed rgba(255,255,255,0.08)' }}>
              <i className="ri-add-line text-base" />
              Özel Görev Ekle
            </button>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3">
        <button onClick={() => onNavigate('ilk4')}
          className="flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold text-white cursor-pointer transition-all hover:opacity-90 whitespace-nowrap"
          style={{ background: 'linear-gradient(135deg, #F5A623 0%, #FF6B35 100%)' }}>
          <i className="ri-user-add-line text-base" />
          Yeni Kişi Ekle
        </button>
        <button onClick={() => onNavigate('whatsapp')}
          className="flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold text-white cursor-pointer transition-all hover:opacity-90 whitespace-nowrap"
          style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' }}>
          <i className="ri-whatsapp-line text-base" />
          WhatsApp Mesaj Gönder
        </button>
        <button
          onClick={() => setStatus(s => s === 'aktif' ? 'pasif' : 'aktif')}
          className="flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold text-white cursor-pointer transition-all hover:bg-white/10 whitespace-nowrap"
          style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <i className="ri-refresh-line text-base" />
          Durum Güncelle
        </button>
      </div>

      {/* Motivational Banner */}
      <div className="rounded-2xl p-5 flex items-center gap-4" style={{ background: 'linear-gradient(135deg, rgba(245,166,35,0.08) 0%, rgba(255,107,53,0.05) 100%)', border: '1px solid rgba(245,166,35,0.15)' }}>
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(245,166,35,0.15)' }}>
          <i className="ri-lightbulb-flash-line text-amber-400 text-xl" />
        </div>
        <div>
          <p className="text-sm font-semibold text-white">1. Adım Stratejisi</p>
          <p className="text-xs text-gray-400 mt-0.5">4 kişinin 1. adımı tamamlaması, aktiflik maliyetini tamamen karşılar ve kâr elde etmeye başlarsın. <span className="text-amber-400 font-medium">{4 - step1Count} adım kaldı!</span></p>
        </div>
      </div>
    </div>
  );
}